import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../index';
import { useAuth } from './AuthContext';

const FavoritesContext = createContext();

const LOCAL_STORAGE_KEY = 'guestFavorites';

export const FavoritesProvider = ({ children }) => {
  const { user, isAnonymous } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const prevIsAnonymousRef = useRef(isAnonymous);

  // Load favorites when auth state changes
  useEffect(() => {
    if (!user) return;

    if (isAnonymous) {
      try {
        const stored = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '[]');
        setFavorites(stored);
      } catch {
        setFavorites([]);
      }
    } else {
      const loadFavorites = async () => {
        setFavoritesLoading(true);
        try {
          const getFavs = httpsCallable(functions, 'api-getFavorites');
          const result = await getFavs();
          if (result.data.success) {
            setFavorites(result.data.favorites.map(f => f.vehicle_id));
          }
        } catch (err) {
          console.error('Error loading favorites:', err);
        } finally {
          setFavoritesLoading(false);
        }
      };
      loadFavorites();
    }
  }, [user, isAnonymous]);

  // Migrate localStorage favorites when user transitions from anonymous to authenticated
  useEffect(() => {
    if (prevIsAnonymousRef.current && !isAnonymous && user) {
      const migrate = async () => {
        try {
          const stored = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '[]');
          if (stored.length === 0) return;

          const addFav = httpsCallable(functions, 'api-addFavorite');
          for (const vehicleId of stored) {
            try {
              await addFav({ vehicleId });
            } catch (err) {
              console.error('Error migrating favorite:', vehicleId, err);
            }
          }
          localStorage.removeItem(LOCAL_STORAGE_KEY);
        } catch (err) {
          console.error('Error migrating favorites:', err);
        }
      };
      migrate();
    }
    prevIsAnonymousRef.current = isAnonymous;
  }, [isAnonymous, user]);

  const isFavorite = useCallback((vehicleId) => {
    return favorites.includes(vehicleId);
  }, [favorites]);

  const toggleFavorite = useCallback(async (vehicleId) => {
    const alreadyFavorited = favorites.includes(vehicleId);

    if (alreadyFavorited) {
      // Remove
      if (isAnonymous) {
        setFavorites(prev => {
          const updated = prev.filter(id => id !== vehicleId);
          localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
          return updated;
        });
      } else {
        try {
          const removeFav = httpsCallable(functions, 'api-removeFavorite');
          await removeFav({ vehicleId });
          setFavorites(prev => prev.filter(id => id !== vehicleId));
        } catch (err) {
          console.error('Error removing favorite:', err);
        }
      }
      return 'removed';
    } else {
      // Add
      if (isAnonymous) {
        setFavorites(prev => {
          const updated = [...prev, vehicleId];
          localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
          return updated;
        });
        return 'local';
      } else {
        try {
          const addFav = httpsCallable(functions, 'api-addFavorite');
          await addFav({ vehicleId });
          setFavorites(prev => [...prev, vehicleId]);
          return 'server';
        } catch (err) {
          console.error('Error adding favorite:', err);
        }
      }
    }
  }, [favorites, isAnonymous]);

  return (
    <FavoritesContext.Provider value={{
      favorites,
      favoritesLoading,
      isFavorite,
      toggleFavorite,
    }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) throw new Error('useFavorites must be used within FavoritesProvider');
  return context;
};
